import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { FormattedMessage, useIntl } from 'react-intl';
import makeStyles from '@mui/styles/makeStyles';
import { useState } from 'react';
import { Feature, getFeatureByAddOn, RecurlyAddOn, Subscription } from '../../../../models';
import { plan } from '../../../../layout/routes';
import { useNavigate, useQuery } from '../../../../routes';
import getFeaturePrefix = Feature.getFeaturePrefix;

const useStyles = makeStyles(theme => ({
  viewAddonsBtn: {
    color: '#5959EB',
    fontWeight: 600,
    fontSize: 14,
    display: 'flex',
    padding: theme.spacing(1, 0),
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    width: '100%',
    textTransform: 'none' as const,
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  addonsTableCell: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 1,
    color: '#808B9A',
    textTransform: 'uppercase' as const,
    padding: theme.spacing(2, 3),
  },
  addonRowCell: {
    fontSize: 14,
    color: '#22283C',
    padding: theme.spacing(2, 3),
    borderBottom: '1px solid #F4F5F9',
  },
  notRenewingBadge: {
    backgroundColor: '#FFFBE6',
    color: '#B7652A',
    fontSize: 11,
    fontWeight: 600,
    height: 20,
    borderRadius: 4,
  },
  addMoreBtn: {
    color: '#5959EB',
    borderColor: '#5959EB',
    fontSize: 12,
    fontWeight: 600,
    '&:hover': {
      borderColor: '#43509B',
      color: '#43509B',
    },
  },
}));

const AddOnRow = (
  {
    addOn,
    feature,
    quota,
    isNotRenewal,
  }: {
    addOn: RecurlyAddOn,
    feature: Feature,
    quota: number,
    isNotRenewal?: boolean,
  },
) => {
  const classes = useStyles();
  const intl = useIntl();

  const { navigate, replaceQuery } = useNavigate();
  const { queryDictionary } = useQuery();

  const handleAddAddon = (feature: Feature) => {
    const prefix = getFeaturePrefix(feature);
    navigate(
      plan.path,
      replaceQuery({
        ...queryDictionary(),
        [`${prefix}Quota`]: 1,
      }),
    );
  };

  return (
    <TableRow
      id={`addon-row-${addOn}`}
      key={`addon-row-${addOn}`}
    >
      <TableCell
        id={`addon-row-${addOn}-product`}
        key={`addon-row-${addOn}-product`}
        className={classes.addonRowCell}
      >
        <Box pl={2}>
          <Typography sx={{ fontSize: 14, color: '#22283C', fontWeight: 500 }}>
            <FormattedMessage id={`addon.${addOn}.display.name`} />
          </Typography>
          {isNotRenewal && (
            <Chip
              label={intl.formatMessage({ id: 'page.subscription.v2.not.renewing.badge' })}
              size="small"
              className={classes.notRenewingBadge}
            />
          )}
        </Box>
      </TableCell>
      <TableCell
        id={`addon-row-${addOn}-quantity`}
        key={`addon-row-${addOn}-quantity`}
        className={classes.addonRowCell}
      >
        <Typography sx={{ fontSize: 14, color: '#22283C' }}>
          <FormattedMessage
            id="page.subscription.current.subscription.quota"
            values={{
              quota,
              unit: intl.formatMessage({
                id: `feature.${feature.toLowerCase()}.unit.plural`,
              }),
            }}
          />
        </Typography>
      </TableCell>
      <TableCell
        id={`addon-row-${addOn}-actions`}
        key={`addon-row-${addOn}-actions`}
        className={classes.addonRowCell}
        sx={{ textAlign: 'center' }}
      >
        {isNotRenewal
          ? (<FormattedMessage id="page.subscription.v2.addons.divide" />)
          : (
            <Button
              variant="outlined"
              size="small"
              className={classes.addMoreBtn}
              onClick={() => handleAddAddon(feature)}
            >
              <FormattedMessage id="page.subscription.v2.addons.add.more.btn" />
            </Button>
          )}
      </TableCell>
    </TableRow>
  );
};

export const AddOnsView = (
  { trueSubscription }: { trueSubscription: Subscription },
) => {
  const classes = useStyles();
  const [showAddons, setShowAddons] = useState(true);

  return (
    <>
      <Button
        disableRipple
        className={classes.viewAddonsBtn}
        onClick={() => setShowAddons(prev => !prev)}
      >
        {showAddons
          ? <KeyboardArrowUpIcon fontSize="small" />
          : <KeyboardArrowDownIcon fontSize="small" />}
        <FormattedMessage id="page.subscription.v2.view.addons.btn" />
      </Button>
      <Collapse in={showAddons}>
        <Table
          id="subscription-addons-table"
          key="subscription-addons-table"
        >
          <TableHead
            id="subscription-addons-table-head"
            key="subscription-addons-table-head"
            sx={theme => ({
              padding: theme.spacing(2, 0),
              backgroundColor: 'rgba(175,185,221,0.22)',
            })}
          >
            <TableRow
              id="subscription-addons-table-head-row"
              key="subscription-addons-table-head-row"
            >
              <TableCell
                id="subscription-addons-table-head-product"
                key="subscription-addons-table-head-product"
                className={classes.addonsTableCell}
              >
                <Box pl={2}>
                  <FormattedMessage id="page.subscription.v2.addons.product.column" />
                </Box>
              </TableCell>
              <TableCell
                id="subscription-addons-table-head-quantity"
                key="subscription-addons-table-head-quantity"
                className={classes.addonsTableCell}
              >
                <FormattedMessage id="page.subscription.v2.addons.quantity.column" />
              </TableCell>
              <TableCell
                id="subscription-addons-table-head-actions"
                key="subscription-addons-table-head-actions"
                className={classes.addonsTableCell}
                sx={{ textAlign: 'center' }}
              >
                <FormattedMessage id="page.subscription.v2.addons.actions.column" />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            id="subscription-addons-table-body"
            key="subscription-addons-table-body"
          >
            {Object.entries(RecurlyAddOn).map(([key, addOn]) => {
              const canceledQuota = trueSubscription.canceledAddOns?.[key] ?? 0;
              const currentQuota = trueSubscription.metadata
                ?.find(metadata => metadata.name === key)?.value ?? 0;
              const feature = getFeatureByAddOn(addOn);
              return (
                <>
                  {currentQuota - canceledQuota > 0 && (
                    <AddOnRow
                      key={`current-${addOn}`}
                      addOn={addOn}
                      feature={feature}
                      quota={currentQuota - canceledQuota}
                    />
                  )}
                  {canceledQuota > 0 && (
                    <AddOnRow
                      key={`canceled-${addOn}`}
                      addOn={addOn}
                      feature={feature}
                      quota={canceledQuota}
                      isNotRenewal
                    />
                  )}
                </>
              );
            })}
          </TableBody>
        </Table>
      </Collapse>
    </>
  );
};
